/**
 * The pre-consultation CI queue — membership and cell derivations.
 *
 * The tab's job is turning a lead with something on the books into a held
 * consult, so membership keys on **an appointment existing** — the PAC intro
 * call, or the consult a lead self-scheduled since Intake 1.1 — and not on
 * `bariatric-procedure-stage`. Two reasons, the second the decisive one:
 *
 * 1. The tag drifts. 124 production patients are mis-tagged `post-consultation`
 *    from the pre-fix `noshow` bug alone, and nothing recomputes the tag when an
 *    appointment's status changes.
 * 2. The tag cannot be fetched at this scale. The stage-tag path pages every
 *    matching Patient to collect ids and passes them comma-joined to GraphQL.
 *    That is survivable for `pre-consultation-paid`; widening to
 *    `pre-consultation-unpaid` — the *Inquiry* bucket, the largest population in
 *    the project — is not. Rooting on the appointment removes the pre-fetch
 *    entirely and lets the date range scope the query server-side.
 *
 * Scoping to `ci-intro-call` alone is what left this queue empty: production has
 * never recorded one, while Intake 1.1 books every self-scheduled consult as
 * `pn-consultation-visit`. The scope is now the intro call plus
 * `CONSULTATION_APPOINTMENT_CODES` — the same list the procedure stage reads.
 *
 * The design doc lists "stage is pre-consultation-unpaid or -paid" as an include
 * alongside "consult already occurred" as an exclude. Given the exclude, the
 * stage row is redundant: a patient whose consult has not occurred has not
 * advanced past pre-consultation by any definition. Only the exclude is
 * implemented, and it is implemented from the appointment.
 */

import { PacIntroCallAnswers } from './patient-access-note';
import { appointmentHasOccurred, isVoidAppointment, ListAppointment } from './pac-list-appointments';

export type { ListAppointment } from './pac-list-appointments';
export { appointmentHasOccurred, isVoidAppointment } from './pac-list-appointments';

/** The three states the Status column renders. */
export type IntroCallStatus = 'Scheduled' | 'Occurred' | 'No-show';

export function introCallStatusLabel(appointment: ListAppointment, now: Date): IntroCallStatus {
  if (appointment.status === 'noshow') {
    return 'No-show';
  }

  return appointmentHasOccurred(appointment, now) ? 'Occurred' : 'Scheduled';
}

/**
 * Membership: something live is on the books and the consult has not happened.
 *
 * A booked-but-not-yet-held consult **stays here**. The hand-off is the consult
 * happening, not the consult being booked — until it does, a cancellation puts
 * the lead straight back into play, and a queue that dropped them at booking
 * would never notice.
 *
 * An intro call is no longer required. Intake 1.1 replaced the PAC-staffed call
 * with a self-scheduled PN consultation, and production has never recorded a
 * single `ci-intro-call` — so requiring one left this queue permanently empty
 * while the patients it describes booked consults every day. Either appointment
 * now admits a lead; the consult having *happened* is still what removes them.
 */
export function isPreConsultationLead(
  lead: { introCall?: ListAppointment; consult?: ListAppointment },
  now: Date
): boolean {
  if (lead.consult && appointmentHasOccurred(lead.consult, now)) {
    return false;
  }

  if (lead.introCall && !isVoidAppointment(lead.introCall)) {
    return true;
  }

  return Boolean(lead.consult && !isVoidAppointment(lead.consult));
}

function readString(value: string | string[] | boolean | undefined): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readStringArray(value: string | string[] | boolean | undefined): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
  }
  const single = readString(value);
  return single ? [single] : [];
}

/*
 * The Location column's reader used to live here as `getIntroCallLocation`, which
 * read only the charted call answer. It now uses `readMarketLocation` from
 * `intake-selection`, so the list, the Intake & billing panel and the intro-call
 * card share one precedence chain: BAR-2591 moved it to the patient extension,
 * and BAR-2597 added the eligibility form as the third link.
 *
 * The column still shows the *market*, not the appointment's facility, which is a
 * different fact under a name this list used to occupy.
 */

export interface LikelihoodCell {
  likelihood?: string;
  barriers: string[];
}

/**
 * Blank for a scheduled or no-showed call, because the question is asked at the
 * *end* of the call. An em-dash there is correct, not missing data.
 *
 * Takes no colour: a `High` on its own is not actionable, since plenty of
 * high-likelihood leads already have a consult booked and need nothing. The
 * signal is *High **and** no consult*, which is a chip, not a cell.
 */
export function getIntroCallLikelihood(answers?: PacIntroCallAnswers): LikelihoodCell {
  return {
    likelihood: readString(answers?.values.likelihood),
    barriers: readStringArray(answers?.values.barrier),
  };
}

/** `High` and nothing booked — the highest-value segment in the queue. */
export function isHighLikelihoodNoConsult(lead: { consult?: ListAppointment; answers?: PacIntroCallAnswers }): boolean {
  if (getIntroCallLikelihood(lead.answers).likelihood !== 'High') {
    return false;
  }

  // "No consult" means nothing live on the books — a cancelled consult leaves
  // them just as unbooked as never having had one, and those are exactly the
  // leads worth chasing.
  return !lead.consult || isVoidAppointment(lead.consult);
}
